import React, { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { User as UserIcon, Users, Building2, CreditCard, Share2 } from 'lucide-react'
import TabNavigation from '../components/settings/TabNavigation'
import ProfileSettings from '../components/settings/ProfileSettings'
import UserManagementSettings from '../components/settings/UserManagementSettings'
import OrganizationSettings from '../components/settings/OrganizationSettings'
import BillingSettings from '../components/settings/BillingSettings'
import ManageAccountsSettings from '../components/settings/ManageAccountsSettings'
import { useAuthStore } from '../stores/authStore'
import {
  canManageUsers,
  canManageOrganization
} from '../utils/permissions'
import { isSubscriptionsEnabled } from '../utils/featureFlags'
import { useNavigationTracking } from '../contexts/AnalyticsContext'

type SettingsTab = 'profile' | 'users' | 'organization' | 'accounts' | 'billing'

interface Tab {
  id: SettingsTab
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const SettingsPage: React.FC = () => {
  const { user } = useAuthStore()
  const { trackSettingsTabViewed } = useNavigationTracking()
  const [searchParams] = useSearchParams()

  // Filter tabs based on user permissions
  const allTabs: Tab[] = [
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'accounts', label: 'Social Accounts', icon: Share2 },
    { id: 'billing', label: 'Billing', icon: CreditCard }
  ]

  const visibleTabs = useMemo(() => {
    return allTabs.filter((tab) => {
      if (tab.id === 'profile') return true
      if (tab.id === 'users') return canManageUsers(user)
      if (tab.id === 'organization') return canManageOrganization(user)
      if (tab.id === 'accounts') return canManageOrganization(user)
      if (tab.id === 'billing') return isSubscriptionsEnabled() && canManageOrganization(user)
      return false
    })
  }, [user])

  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const tabParam = searchParams.get('tab') as SettingsTab | null
    if (tabParam && visibleTabs.some(t => t.id === tabParam)) return tabParam
    return visibleTabs.length > 0 ? visibleTabs[0].id : 'profile'
  })

  const handleTabChange = (tabId: string) => {
    trackSettingsTabViewed(tabId)
    setActiveTab(tabId as SettingsTab)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />
      case 'users':
        return canManageUsers(user) ? (
          <UserManagementSettings />
        ) : (
          <ProfileSettings />
        )
      case 'organization':
        return canManageOrganization(user) ? (
          <OrganizationSettings />
        ) : (
          <ProfileSettings />
        )
      case 'accounts':
        return canManageOrganization(user) ? (
          <ManageAccountsSettings />
        ) : (
          <ProfileSettings />
        )
      case 'billing':
        return canManageOrganization(user) ? (
          <BillingSettings />
        ) : (
          <ProfileSettings />
        )
      default:
        return <ProfileSettings />
    }
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      <div className='h-full'>
        {/* Header Section */}
        <div className='mb-6'>
          {/* Page Title */}
          <div className='flex items-center space-x-3 mb-2'>
            <h1 className='text-xl font-bold text-gray-900 tracking-tight'>
              Settings
            </h1>
          </div>
          <p className='text-sm text-gray-600'>
            Manage your account settings and preferences
          </p>
        </div>

        {/* Main Content Card */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
          <TabNavigation
            tabs={visibleTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          <div className='p-6'>{renderTabContent()}</div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
